import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
} from "react";

import { type UseQueryResult, useQuery } from "@tanstack/react-query";

import {
  type IdeaUpdatePayload,
  mergePendingIdeaPayloads,
} from "simplydo/core/ideaUpdates";
import { initUseQuery } from "simplydo/core/query";

import api from "api";

import type { IdeaDetail, UseIdeaOptions } from "../types";

/**
 * Unsaved edits made by the idea editor. Readers see them applied on top of the
 * cached idea, so edits render synchronously and the cache only holds what the
 * server has confirmed.
 */
export const createIdeaDraft = () => {
  let edits: IdeaUpdatePayload = {};
  const listeners = new Set<() => void>();
  return {
    get: () => edits,
    set: (nextEdits: IdeaUpdatePayload) => {
      edits = nextEdits;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export type IdeaDraft = ReturnType<typeof createIdeaDraft>;

/**
 * The IdeaContext provides the useIdea hooks with the current ids required to fetch the idea and its child.
 */
export const IdeaContext = createContext<{
  ideaId: string | undefined;
  setIdeaId: Dispatch<SetStateAction<string | undefined>>;
  childId: string | undefined | null;
  setChildId: Dispatch<SetStateAction<string | undefined | null>>;
  draft: IdeaDraft;
}>({
  ideaId: undefined,
  setIdeaId: () => {},
  childId: undefined,
  setChildId: () => {},
  draft: createIdeaDraft(),
});

export type IdeaIdState = readonly [
  string | undefined,
  Dispatch<SetStateAction<string | undefined>>,
];

export type ChildIdState = readonly [
  string | undefined | null,
  Dispatch<SetStateAction<string | undefined | null>>,
];

/** Selected shape when `useIdea` / `useChildIdea` is called with or without `select`. */
export type UseIdeaSelected<T> = T extends undefined ? IdeaDetail : T;

export type UseIdeaResult<T = undefined> = readonly [
  UseIdeaSelected<T>,
  UseQueryResult<UseIdeaSelected<T>, Error>,
];

export const useIdeaIdState = (): IdeaIdState => {
  const ideaContext = useContext(IdeaContext);

  return [ideaContext.ideaId, ideaContext.setIdeaId] as const;
};

export const useChildIdState = (): ChildIdState => {
  const ideaContext = useContext(IdeaContext);
  return [ideaContext.childId, ideaContext.setChildId] as const;
};

export const useIdeaDraft = () => useContext(IdeaContext).draft;

const noEdits: IdeaUpdatePayload = {};

export const useIdeaEdits = (ideaId: string | undefined) => {
  const { ideaId: editedIdeaId, draft } = useContext(IdeaContext);
  const edits = useSyncExternalStore(draft.subscribe, draft.get);
  return ideaId === editedIdeaId ? edits : noEdits;
};

const useIdeaQuery = <T,>(
  ideaId: string | undefined,
  options?: UseIdeaOptions<T>,
): UseQueryResult<UseIdeaSelected<T>, Error> => {
  const edits = useIdeaEdits(ideaId);
  const select = options?.select;

  return useQuery(
    initUseQuery(api.ideas.get, [ideaId ?? ""], {
      isEnabled: !!ideaId,
      select: (data: IdeaDetail) => {
        const idea = mergePendingIdeaPayloads(data, edits);
        return select ? select(idea) : idea;
      },
    }),
  ) as UseQueryResult<UseIdeaSelected<T>, Error>;
};

/**
 * The useIdea hook will always return the anchoring parent idea, no matter whether an idea is merged or not. This is useful for merged ideas when you need to access the parent idea data.
 */
export const useIdea = <_X, T = undefined>(
  options?: UseIdeaOptions<T>,
): UseIdeaResult<T> => {
  const [ideaId, setParentId] = useIdeaIdState();
  const [childId, setChildId] = useChildIdState();

  const ideaQuery = useIdeaQuery<T>(ideaId, options);
  const ideaData = ideaQuery.data as IdeaDetail | undefined;

  useEffect(() => {
    if (ideaData?.parent) {
      setParentId(ideaData.parent);
      setChildId(ideaData?._id);
      return;
    }
    if (ideaData?.children && ideaData.children.length > 0) {
      setChildId((prev) => (!prev ? ideaData.children![0] : prev));
    }
  }, [
    childId,
    ideaData?.children,
    ideaData?.parent,
    ideaData?._id,
    setChildId,
    setParentId,
  ]);

  return [ideaData as UseIdeaSelected<T>, ideaQuery] as const;
};

/**
 * The useChildIdea hook will return either a child of an idea (if the idea is merged) otherwise it will also return the idea itself. This way this hook can be used to render the content of a child or the idea itself without requiring conditional logic in the component.
 */
export const useChildIdea = <_X, T = undefined>(
  options?: UseIdeaOptions<T>,
): UseIdeaResult<T> => {
  const [ideaId] = useIdeaIdState();
  const [childId] = useChildIdState();

  const ideaQuery = useIdeaQuery<T>(childId ?? ideaId, options);

  return [ideaQuery.data as UseIdeaSelected<T>, ideaQuery] as const;
};
